import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { useDentalHealthStats } from '../../hooks/useDentalHealthStats';
import LoadingSpinner from '../common/LoadingSpinner';
import StatsCard from '../common/StatsCard';
import { formatCurrency, formatNumber } from '../../utils/formatters';

interface Props {
  dateRange: { start_date?: string; end_date?: string };
}

const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#84cc16'];

export default function DentalHealthTab({ dateRange }: Props) {
  const { data: stats, isLoading, error } = useDentalHealthStats(dateRange);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">Erreur lors du chargement des données</div>;
  if (!stats) return null;

  // Données pour l'analyse épidémiologique
  const epidemiologyData = stats.pathology_distribution.map(item => ({
    name: item.pathology_name,
    cases: item.count,
    percentage: item.percentage,
    severity: item.avg_severity || 1
  }));

  // Données par tranche d'âge
  const ageGroupData = stats.age_group_analysis.map(group => ({
    age_group: group.age_range,
    patients: group.patient_count,
    treatments: group.treatment_count,
    avg_cost: group.avg_treatment_cost
  }));

  return (
    <div className="space-y-6">
      {/* KPIs Santé Dentaire */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Pathologies Courantes"
          value={formatNumber(stats.total_pathologies)}
          icon="🦷"
          color="purple"
        />
        <StatsCard
          title="Taux de Prévention"
          value={`${stats.prevention_rate.toFixed(1)}%`}
          icon="🛡️"
          color={stats.prevention_rate >= 60 ? "green" : stats.prevention_rate >= 40 ? "yellow" : "red"}
        />
        <StatsCard
          title="Urgences/Mois"
          value={formatNumber(stats.emergency_rate)}
          icon="🚨"
          color="red"
        />
        <StatsCard
          title="Suivi Régulier"
          value={`${stats.regular_followup_rate.toFixed(1)}%`}
          icon="📅"
          color="blue"
        />
      </div>

      {/* Analyses principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribution des pathologies */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Distribution des Pathologies Dentaires</h3>
          <PieChart width={500} height={350}>
            <Pie
              data={epidemiologyData}
              cx={250}
              cy={175}
              labelLine={false}
              label={({ name, percentage }) => `${name} ${percentage.toFixed(1)}%`}
              outerRadius={120}
              fill="#8884d8"
              dataKey="cases"
            >
              {epidemiologyData.map((entry, index: number) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [formatNumber(Number(value)), 'Cas']} />
          </PieChart>
        </div>

        {/* Analyse par âge */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Analyse par Tranche d'Âge</h3>
          <BarChart width={500} height={350} data={ageGroupData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="age_group" />
            <YAxis />
            <Tooltip formatter={(value, name) => [
              name === 'avg_cost' ? formatCurrency(Number(value)) : formatNumber(Number(value)),
              name === 'patients' ? 'Patients' : name === 'treatments' ? 'Traitements' : 'Coût Moyen'
            ]} />
            <Legend />
            <Bar dataKey="patients" fill="#8884d8" name="Patients" />
            <Bar dataKey="treatments" fill="#82ca9d" name="Traitements" />
          </BarChart>
        </div>
      </div>

      {/* Tendances saisonnières des pathologies */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Tendances Saisonnières des Pathologies</h3>
        <LineChart width={1000} height={400} data={stats.seasonal_pathology_trends}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="caries" stroke="#8884d8" strokeWidth={2} name="Caries" />
          <Line type="monotone" dataKey="gingivitis" stroke="#82ca9d" strokeWidth={2} name="Gingivite" />
          <Line type="monotone" dataKey="urgencies" stroke="#ff7300" strokeWidth={2} name="Urgences" />
          <Line type="monotone" dataKey="preventive" stroke="#00ff00" strokeWidth={2} name="Préventif" />
        </LineChart>
      </div>

      {/* Profil épidémiologique radar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Profil Épidémiologique</h3>
          <RadarChart width={500} height={350} data={stats.epidemiology_profile}>
            <PolarGrid />
            <PolarAngleAxis dataKey="indicator" />
            <PolarRadiusAxis angle={90} domain={[0, 100]} />
            <Radar
              name="Score Santé"
              dataKey="score"
              stroke="#8884d8"
              fill="#8884d8"
              fillOpacity={0.6}
            />
            <Tooltip />
          </RadarChart>
        </div>

        {/* Statistiques détaillées */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Indicateurs de Santé Publique</h3>
          <div className="space-y-4">
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-medium text-gray-900">Hygiène Dentaire</h4>
              <p className="text-2xl font-bold text-green-600">{stats.hygiene_score.toFixed(1)}/10</p>
              <p className="text-sm text-gray-500">Score moyen d'hygiène</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-medium text-gray-900">Accès aux Soins</h4>
              <p className="text-2xl font-bold text-blue-600">{stats.care_access_rate.toFixed(1)}%</p>
              <p className="text-sm text-gray-500">Population ayant accès aux soins</p>
            </div>
            <div className="border-l-4 border-yellow-500 pl-4">
              <h4 className="font-medium text-gray-900">Coût Moyen Soins</h4>
              <p className="text-2xl font-bold text-yellow-600">{formatCurrency(stats.avg_care_cost)}</p>
              <p className="text-sm text-gray-500">Par patient et par visite</p>
            </div>
            <div className="border-l-4 border-red-500 pl-4">
              <h4 className="font-medium text-gray-900">Pathologies Sévères</h4>
              <p className="text-2xl font-bold text-red-600">{stats.severe_cases_rate.toFixed(1)}%</p>
              <p className="text-sm text-gray-500">Cas nécessitant chirurgie</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recommandations de santé publique */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🏥 Recommandations de Santé Publique</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">🦷 Prévention Primaire</h4>
            <p className="text-sm text-gray-600">
              Renforcer les campagnes d'éducation à l'hygiène dentaire dans les écoles et communautés.
            </p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">📊 Dépistage Précoce</h4>
            <p className="text-sm text-gray-600">
              Organiser des campagnes de dépistage pour identifier les pathologies en phase initiale.
            </p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">💰 Accessibilité Financière</h4>
            <p className="text-sm text-gray-600">
              Développer des programmes de soins subventionnés pour les populations vulnérables.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}